/**
 * 把客户背景拼成一条「按意见改写」的意见（草案 §4.2 / §9）。
 *
 * 规则：
 *   - 禁区放在最前面，以「请避免涉及：」开头——超长截断时从尾部砍，禁区永远保得住。
 *     填「无」之类的不拼。
 *   - 其余按对出题有用的程度排：讲述人基本信息 → 关键人物 → 经历 → 高光与转折 → 时代印记 → 写给谁 → 老人的情况。
 *     交付日期、本数、操作方式、已有素材、风格偏好与出题无关，不拼。
 *   - 总长不超过 ADVICE_MAX_LENGTH（与服务端一致），拼出来只是草稿，销售可以再改。
 */
import { ADVICE_MAX_LENGTH, type AdvisorBackground } from '../../../api/advisor';

const NO_TABOO = new Set(['无', '没有', '暂无', '无禁区', '没有禁区', '-', '/']);

/** 多行 / 多空白压成一行；去掉结尾标点，由拼接处统一补句号 */
function clean(v: unknown): string {
  if (typeof v !== 'string' && typeof v !== 'number') return '';
  return String(v)
    .replace(/\s*[\r\n]+\s*/g, '；')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/[；;，,。.、\s]+$/u, '');
}

/** 按 UTF-16 长度截断（服务端也按 .length 计），不劈开代理对 */
function cut(s: string, max: number): string {
  if (s.length <= max) return s;
  let out = s.slice(0, max);
  const last = out.charCodeAt(out.length - 1);
  if (last >= 0xd800 && last <= 0xdbff) out = out.slice(0, -1);
  return out;
}

export function buildAdviceFromBackground(input: {
  narratorName?: string | null;
  narratorRelation?: string | null;
  background?: AdvisorBackground | null;
}): string {
  const bg = input.background ?? {};
  const parts: string[] = [];

  const taboos = clean(bg.taboos);
  if (taboos && !NO_TABOO.has(taboos)) parts.push(`请避免涉及：${taboos}。`);

  const name = clean(input.narratorName);
  const relation = clean(input.narratorRelation);
  const who = [name, relation && `（${relation}）`].filter(Boolean).join('');
  const basics = [
    who && `讲述人${who}`,
    clean(bg.birthYear) && `${clean(bg.birthYear).replace(/年$/u, '')}年生`,
    clean(bg.birthPlace) && `籍贯${clean(bg.birthPlace)}`,
    clean(bg.residence) && `现居${clean(bg.residence)}`,
  ].filter(Boolean);

  const facts: string[] = [];
  if (basics.length) facts.push(`${basics.join('，')}。`);
  const labelled: Array<[string, unknown]> = [
    ['关键人物', bg.keyPeople],
    ['经历', bg.lifeSkeleton],
    ['高光与转折', bg.highlights],
    ['时代印记', bg.eraMarks],
    ['写给', bg.writtenFor],
    ['老人的情况', bg.conditions],
  ];
  for (const [label, value] of labelled) {
    const v = clean(value);
    if (v) facts.push(`${label}：${v}。`);
  }

  if (facts.length) parts.push(`结合讲述人的背景把问题问具体，可直接用人名、地名：${facts.join('')}`);

  return cut(parts.join(''), ADVICE_MAX_LENGTH);
}
